import { fieldLabel } from "./analysisFieldMeta";
import {
  DEPLOYMENT_MODEL_FIELD_ID,
  formatDeploymentModel,
  formatDisplayToken,
  formatObjectEntry,
  formatPowerSupply,
  formatRequestedBoolean,
  isPowerSupplyField,
  isRequestedBooleanField,
} from "./analysisDisplayUtils";

export const USEFUL_STATUSES = new Set(["found", "not_found", "ambiguous", "inferred"]);
export const PRESENT_STATUSES = new Set(["found", "inferred", "ambiguous"]);

const GAP_FIELD_SUFFIXES = new Set(["design_gaps", "sti_gaps", "commercial_admin_gaps"]);
const DEPLOYMENT_MODE_FIELD_RE = /(^|\.)(deployment_mode|deployment_model|project_type_cloud_onprem_hybrid|onsite_or_online|certified_or_knowledge_transfer)$/;

export function isFieldResult(obj) {
  return Boolean(obj && typeof obj === "object" && typeof obj.status === "string" && "value" in obj);
}

/** Walk nested analysis objects and collect leaf field results. */
export function collectFieldResults(node, prefix = "") {
  if (!node || typeof node !== "object") return [];
  if (isFieldResult(node)) {
    const fieldId = prefix.replace(/^\./, "");
    return fieldId ? [{ fieldId, result: node }] : [];
  }
  const out = [];
  for (const [key, value] of Object.entries(node)) {
    if (GAP_FIELD_SUFFIXES.has(key)) continue;
    const next = prefix ? `${prefix}.${key}` : key;
    out.push(...collectFieldResults(value, next));
  }
  return out;
}

export function getNestedValue(root, path) {
  if (!root || !path?.length) return undefined;
  let node = root;
  for (const key of path) {
    if (node == null || typeof node !== "object") return undefined;
    node = node[key];
  }
  return node;
}

export function isSectionOutOfScope(fieldResults) {
  if (!fieldResults?.length) return false;
  return fieldResults.every(({ result }) => result?.status === "not_applicable");
}

/** True when a section has no found/inferred/ambiguous values (all missing or N/A). */
export function isSectionWithoutPresentValues(fieldResults) {
  if (!fieldResults?.length) return true;
  return fieldResults.every(({ result }) => !PRESENT_STATUSES.has(result?.status));
}

export function hasUsefulContent(fieldResults) {
  return fieldResults.some(({ result }) => USEFUL_STATUSES.has(result?.status));
}

export function shouldShowField(result, showOutOfScope) {
  if (!result) return false;
  // Missing values belong in Key Gaps, not in every detail card.
  if (result.status === "not_found") return false;
  if (result.status === "not_applicable") return showOutOfScope;
  return USEFUL_STATUSES.has(result.status);
}

export function statusLabel(status, language = "en") {
  const es = language === "es";
  const pt = language === "pt";
  const map = {
    found: es ? "Encontrado" : pt ? "Encontrado" : "Found",
    not_found: es ? "No encontrado en el RFP" : pt ? "Não encontrado no RFP" : "Not found in RFP",
    not_applicable: es ? "Fuera de alcance" : pt ? "Fora do escopo" : "Out of scope",
    ambiguous: es ? "Requiere revisión" : pt ? "Requer revisão" : "Needs review",
    inferred: es ? "Inferido" : pt ? "Inferido" : "Inferred",
  };
  return map[status] || status;
}

export function statusChipClass(status) {
  switch (status) {
    case "found":
      return "bg-emerald-100 text-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-200";
    case "not_found":
      return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
    case "ambiguous":
      return "bg-amber-100 text-amber-900 dark:bg-amber-950/50 dark:text-amber-200";
    case "inferred":
      return "bg-sky-100 text-sky-800 dark:bg-sky-950/50 dark:text-sky-200";
    case "not_applicable":
      return "bg-slate-100 text-slate-500 dark:bg-slate-800/60 dark:text-slate-400";
    default:
      return "bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300";
  }
}

export function formatFieldValue(value, result, language = "en", fieldId = "") {
  if (value === null || value === undefined) {
    if (result?.status === "not_found") return statusLabel("not_found", language);
    if (result?.status === "not_applicable") return statusLabel("not_applicable", language);
    return "—";
  }
  if (fieldId === DEPLOYMENT_MODEL_FIELD_ID && result?.status === "found") {
    return formatDeploymentModel(value, language);
  }
  if (DEPLOYMENT_MODE_FIELD_RE.test(fieldId) && result?.status === "found" && typeof value === "string") {
    return formatDisplayToken(value, language);
  }
  if (isPowerSupplyField(fieldId) && result?.status === "found") {
    return formatPowerSupply(value, language);
  }
  if (typeof value === "boolean") {
    if (result?.status === "found" && isRequestedBooleanField(fieldId)) {
      return formatRequestedBoolean(value, language);
    }
    const es = language === "es";
    const pt = language === "pt";
    return value ? (es ? "Sí" : pt ? "Sim" : "Yes") : es ? "No" : pt ? "Não" : "No";
  }
  if (Array.isArray(value)) {
    return value.map((v) => {
      if (v && typeof v === "object" && !Array.isArray(v)) {
        return Object.entries(v)
          .map(([k, entryVal]) => formatObjectEntry(k, entryVal, language))
          .join(" · ");
      }
      return typeof v === "string" ? formatDisplayToken(v, language) : String(v);
    });
  }
  if (typeof value === "object") {
    return Object.entries(value).map(([k, v]) => formatObjectEntry(k, v, language));
  }
  if (typeof value === "string") {
    return formatDisplayToken(value, language);
  }
  return String(value);
}

export function showStatusChip(status) {
  return status === "ambiguous" || status === "inferred";
}

export function collectHiddenSectionNames(sections, analysis, showOutOfScope) {
  if (showOutOfScope) return [];
  return sections
    .filter((section) => {
      const node = getNestedValue(analysis, section.path);
      const fields = collectFieldResults(node, section.path.join("."));
      return fields.length > 0 && isSectionOutOfScope(fields);
    })
    .map((s) => s.label);
}

export function collectKeyGaps(analysis, language = "en", limit = 8) {
  const sources = [
    { path: ["design", "design_gaps"], area: "Design" },
    { path: ["support_training_implementation", "sti_gaps"], area: "Support / Training / Implementation" },
    { path: ["commercial_administrative", "commercial_admin_gaps"], area: "Commercial / Administrative" },
  ];
  const gaps = [];
  for (const { path, area } of sources) {
    const result = getNestedValue(analysis, path);
    if (!isFieldResult(result) || result.status !== "found" || !Array.isArray(result.value)) continue;
    for (const entry of result.value) {
      gaps.push({
        fieldId: path.join("."),
        label: area,
        text: String(entry),
      });
    }
  }
  return gaps.slice(0, limit);
}

export function collectWarningFields(analysis) {
  const items = [];
  const walk = (node, prefix = "") => {
    if (!node || typeof node !== "object") return;
    if (isFieldResult(node)) {
      const warnings = (node.warnings || []).filter(Boolean);
      const isQualityIssue =
        node.status === "ambiguous" ||
        node.status === "inferred" ||
        node.confidence === "low" ||
        warnings.length > 0;
      if (isQualityIssue && node.status !== "not_found" && node.status !== "not_applicable") {
        items.push({
          fieldId: prefix.replace(/^\./, ""),
          label: fieldLabel(prefix.replace(/^\./, "")),
          result: node,
        });
      }
      return;
    }
    for (const [key, value] of Object.entries(node)) {
      const next = prefix ? `${prefix}.${key}` : key;
      walk(value, next);
    }
  };
  walk(analysis);
  return items;
}

export function analysisLanguage(analysis) {
  const lang = (analysis?.language || "en").toLowerCase();
  if (lang.startsWith("es")) return "es";
  if (lang.startsWith("pt")) return "pt";
  return "en";
}

export function qualitySummaryText(summary, language = "en") {
  if (!summary || typeof summary !== "object") return "";
  const es = language === "es";
  const found = summary.found ?? 0;
  const missing = summary.not_found ?? 0;
  const low = summary.low_confidence ?? 0;
  if (es) {
    return `Encontrados: ${found} · Faltantes: ${missing} · Baja confianza: ${low}`;
  }
  if (language === "pt") {
    return `Encontrados: ${found} · Ausentes: ${missing} · Baixa confiança: ${low}`;
  }
  return `Found: ${found} · Missing: ${missing} · Low confidence: ${low}`;
}
